import { apiNWL, apiController, restartApiCotroller } from '@/utils/nwlApi';
import store from '@/redux/store';
import { setShowLoader } from '@/redux/slices/loaderSlice';
import { quoteBigIntFields } from '@/utils/bigInt-json';

const getDigitalTwins = async (orgId, locId) => {
	try {
		restartApiCotroller();

		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/digital-twins?offset=0&limit=1&enabled=true`,
			{
				signal: apiController.signal,
			},
		);
		return response.data.data?.[0] || {};
	} catch (error) {
		console.error(error);
	}
};

const getDigitalTwinsNextSimulation = async (orgId, locId, dtId) => {
	try {
		restartApiCotroller();
		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations/next`,
			{
				signal: apiController.signal,
			},
		);
		return response.data || {};
	} catch (error) {
		console.error(error);
	}
};

const getDigitalTwinsSimulations = async (orgId, locId, dtId) => {
	try {
		restartApiCotroller();
		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations?offset=0&limit=2`,
			{
				signal: apiController.signal,
				responseType: 'text',
				transformResponse: [(d) => d],
				transitional: { forcedJSONParsing: false },
			},
		);
		const text = String(response.data);
		const safe = quoteBigIntFields(text, ['simulationId']);
		return JSON.parse(safe);
	} catch (error) {
		console.error(error);
	}
};

const getAllSimulations = async (orgId, locId, dtId) => {
	try {
		restartApiCotroller();
		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations`,
			{
				signal: apiController.signal,
				responseType: 'text',
				transformResponse: [(d) => d],
				transitional: { forcedJSONParsing: false },
			},
		);
		const text = String(response.data);
		const safe = quoteBigIntFields(text, ['simulationId']);
		return JSON.parse(safe);
	} catch (error) {
		console.error(error);
	}
};

const getSimulationTimeline = ({
	orgId,
	locId,
	dtId,
	page = 1,
	pageSize = 10,
	startDate,
	endDate,
	mode,
	locIds,
}) => {
	restartApiCotroller();
	store.dispatch(setShowLoader(false));
	const offset = (page - 1) * pageSize;

	const params = new URLSearchParams();
	params.set('offset', String(offset));
	params.set('limit', String(pageSize));

	if (startDate != null) params.set('start', String(startDate));
	if (endDate != null) params.set('end', String(endDate));
	if (mode) params.set('strategy', mode);
	if (locIds) params.set('locIds', locIds);

	const url = `/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations/timeline?${params.toString()}`;

	const timeline = async () =>
		await apiNWL.get(url, {
			signal: apiController.signal,
		});

	return {
		timeline: timeline().then((r) => r.data),
	};
};

const downloadSimulationTimeline = ({
	orgId,
	locId,
	dtId,
	startDate,
	endDate,
	mode,
	locIds,
	limit,
}) => {
	restartApiCotroller();
	store.dispatch(setShowLoader(false));

	const params = new URLSearchParams();

	if (startDate != null) params.set('start', String(startDate));
	if (endDate != null) params.set('end', String(endDate));
	if (mode) params.set('strategy', mode);
	if (locIds) params.set('locIds', locIds);
	if (limit) params.set('limit', Number(limit));

	const url =
		`/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations/timeline` +
		`?${params.toString()}`;

	const timeline = async () =>
		await apiNWL.get(url, {
			signal: apiController.signal,
			responseType: 'text/csv',
			headers: {
				Accept: 'text/csv',
			},
		});

	return {
		timeline: timeline().then((r) => r.data || {}),
	};
};

const getStrategies = async (orgId, locId) => {
	try {
		restartApiCotroller();
		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/simulations/strategies`,
			{
				signal: apiController.signal,
			},
		);
		return response.data || {};
	} catch (error) {
		console.error(error);
	}
};

const getKpis = async (orgId, locId, dtId, simId) => {
	try {
		restartApiCotroller();
		store.dispatch(setShowLoader(false));
		const response = await apiNWL.get(
			`/organisations/${orgId}/locations/${locId}/digital-twins/${dtId}/simulations/${simId}/kpis`,
			{
				signal: apiController.signal,
			},
		);
		return response.data || {};
	} catch (error) {
		console.error(error);
	}
};

export {
	getDigitalTwins,
	getDigitalTwinsNextSimulation,
	getDigitalTwinsSimulations,
	getAllSimulations,
	getSimulationTimeline,
	downloadSimulationTimeline,
	getStrategies,
	getKpis,
};
